import axios from "axios"
import {v4 as uuidv4} from "uuid"

import {DYN_CONF_KEY_CONTRACT_ID} from "../../../constants/dynamicConfigurationKeys"
import {
    AUTH_ENDPOINTS,
    CONNECT_USER_ENDPOINT,
    GET_USER_INFO_ENDPOINT,
    PASSWORD_ENDPOINTS,
    REQUEST_NEW_MFA_CODE_ENDPOINT,
} from "../../../constants/endpoints"
import {LOGIN_MINIMUM_DELAY} from "../../../constants/forms"
import httpMethod from "../../../constants/httpMethods"
import {RequestInfo, RequestKeys} from "../../../constants/requests"
import {getErrorMessage} from "../../../services/api"
import {addTokenToHeader, removeTokenFromHeader} from "../../../services/configureAxios"
import {addTokenServiceAPIToHeader, removeTokenServiceAPIFromHeader} from "../../../services/configureServiceAPIAxios"
import * as sessionStorageService from "../../../services/sessionStorage"
import {REQUEST, REQUEST_FAILURE, REQUEST_SUCCESS} from "../../../store/requests/requestsReducer"
import {genericThunk} from "../../actionsUtils"
import {saveDynamicConfiguration} from "../../localStorage/dynamicConfiguration/dynamicConfigurationActions"
import {addNotification} from "../../notifications/notificationsActions"
import {
    CHANGE_PASSWORD_FAILURE,
    CHANGE_PASSWORD_REQUEST,
    CHANGE_PASSWORD_SUCCESS,
    CONNECT_USER_FAILURE,
    CONNECT_USER_REQUEST,
    CONNECT_USER_SUCCESS,
    DISCONNECT_USER,
    GET_USER_INFO_FAILURE,
    GET_USER_INFO_REQUEST,
    GET_USER_INFO_SUCCESS,
    SET_BYPASSED_AUTHENTICATION,
} from "./userActionTypes"

/**
 * Thunk to connect a user.
 * Dispatch on request and on success or failure.
 * @param login the user name
 * @param password the user password
 * @param typeEspace the application area where you want to connect
 */
export const connectUser =
    (login, password, typeEspace, confirmation = null, startTime = null) =>
    dispatch => {
        dispatch({
            type: CONNECT_USER_REQUEST,
        })

        // Build request body.
        const body = {
            login,
            password,
            typeEspace,
        }

        // Calcul du temps de saisie du formulaire
        const totalTime = startTime ? Date.now() - startTime : null
        // La valeur confirmation provient d'un champ caché dans la page d'inscription, pour tester si le formulaire est rempli par un robot
        if (confirmation !== null || (totalTime && totalTime < LOGIN_MINIMUM_DELAY)) {
            dispatch(
                addNotification({
                    id: uuidv4(),
                    type: "error",
                    header: "Erreur lors de l'envoi du formulaire",
                    content: "Veuillez réessayer",
                    autoDismiss: true,
                }),
            )

            dispatch({
                type: CONNECT_USER_FAILURE,
                message: "Erreur lors de l'envoi du formulaire",
            })
            removeTokenFromHeader()
            removeTokenServiceAPIFromHeader()

            return Promise.reject()
        } else {
            return axios.post(CONNECT_USER_ENDPOINT, body).then(
                response => {
                    addTokenToHeader(response.data.token)
                    addTokenServiceAPIToHeader(response.data.token)
                    dispatch({
                        type: CONNECT_USER_SUCCESS,
                        response: response.data,
                    })

                    return Promise.resolve(response.data)
                },
                error => {
                    dispatch({
                        type: CONNECT_USER_FAILURE,
                        message: getErrorMessage(error),
                    })
                    removeTokenFromHeader()
                    removeTokenServiceAPIFromHeader()
                    return Promise.reject(error)
                },
            )
        }
    }

/**
 * Thunk to disconnect an user.
 */
export const disconnectUser = () => dispatch => {
    dispatch({
        type: DISCONNECT_USER,
    })
    removeTokenFromHeader()
    removeTokenServiceAPIFromHeader()
    sessionStorageService.clearState()
    return Promise.resolve()
}

export const requestNewMfaCode = mfaSessionId => dispatch => {
    dispatch({
        type: REQUEST,
        requestKeyName: RequestInfo[RequestKeys.NEW_MFA_CODE_REQUEST].name,
    })

    if (!mfaSessionId) {
        return Promise.reject("Session MFA expirée ou invalide.")
    }

    const body = {sessionMfaId: mfaSessionId}

    return axios.post(REQUEST_NEW_MFA_CODE_ENDPOINT, body).then(
        response => {
            dispatch({
                type: REQUEST_SUCCESS,
                requestKeyName: RequestInfo[RequestKeys.NEW_MFA_CODE_REQUEST].name,
                payload: response,
            })
            return Promise.resolve()
        },
        error => {
            dispatch({
                type: REQUEST_FAILURE,
                requestKeyName: RequestInfo[RequestKeys.NEW_MFA_CODE_REQUEST].name,
                message: getErrorMessage(error),
            })
            return Promise.reject(getErrorMessage(error))
        },
    )
}

/**
 * Thunk to retrieve connected user information
 */

export const connectedUserInfo = () => dispatch => {
    dispatch({
        type: GET_USER_INFO_REQUEST,
    })

    return axios.get(GET_USER_INFO_ENDPOINT).then(
        response => {
            dispatch({
                type: GET_USER_INFO_SUCCESS,
                response: response.data,
            })

            if (response.data.idContrat) {
                dispatch(saveDynamicConfiguration(DYN_CONF_KEY_CONTRACT_ID, response.data.idContrat))
            }

            return Promise.resolve(response.data)
        },
        error => {
            dispatch({
                type: GET_USER_INFO_FAILURE,
                message: getErrorMessage(error),
            })

            return Promise.reject(error)
        },
    )
}

/**
 * Thunk to start the recover login/password process
 */
export const recoverLoginPassword = (typeEspace, login, siret, birthDate) => dispatch => {
    const params = {
        typeEspace,
        login,
        siret,
        birthDate,
    }

    const url = AUTH_ENDPOINTS.recoverLoginPassword

    return new Promise((resolve, reject) => {
        genericThunk(
            null,
            httpMethod.GET,
            dispatch,
            url,
            params,
            RequestInfo[RequestKeys.RECOVER_LOGIN_PASSWORD].name,
            null,
        )
            .then(response => resolve(response))
            .catch(error => reject(error))
    })
}

export const changePassword = (newPassword, resetPasswordToken) => dispatch => {
    dispatch({
        type: CHANGE_PASSWORD_REQUEST,
    })

    const body = {
        nouveauMotDePasse: newPassword,
        resetPasswordToken,
    }

    return axios.post(PASSWORD_ENDPOINTS.changePassword, body).then(
        response => {
            dispatch({
                type: CHANGE_PASSWORD_SUCCESS,
            })

            return Promise.resolve(response)
        },
        error => {
            dispatch({
                type: CHANGE_PASSWORD_FAILURE,
                message: getErrorMessage(error),
            })

            return Promise.reject(error)
        },
    )
}

/**
 * Thunk to give the possibility to a user to change his password into his authentication informations
 * @param oldPassword   oldPassword user
 * @param newPassword   newPassword user
 * @param newPasswordConfirmation   newPasswordConfirmation user
 *  */
export const changeActualPassword = (oldPassword, newPassword, newPasswordConfirmation) => dispatch => {
    dispatch({
        type: REQUEST,
        requestKeyName: RequestInfo[RequestKeys.CHANGE_ACTUAL_PASSWORD].name,
    })

    const body = {
        ancienMotDePasse: oldPassword,
        nouveauMotDePasse: newPassword,
        nouveauMotDePasseConfirmation: newPasswordConfirmation,
    }

    return axios.patch(PASSWORD_ENDPOINTS.changeActualPassword, body).then(
        () => {
            dispatch({
                type: REQUEST_SUCCESS,
                requestKeyName: RequestInfo[RequestKeys.CHANGE_ACTUAL_PASSWORD].name,
            })

            return Promise.resolve()
        },
        error => {
            const message = getErrorMessage(error)
            dispatch({
                type: REQUEST_FAILURE,
                requestKeyName: RequestInfo[RequestKeys.CHANGE_ACTUAL_PASSWORD].name,
                message,
            })

            return Promise.reject(message)
        },
    )
}

/**
 * Thunk to mimiq user connection.
 * Dispatch on request and on success or failure.
 * @param sourceId the ciphered user id
 * @param userLogin the ciphered user login
 * @param areaType the application area where you want to connect
 * @param bypassToken the token used to bypass the login
 */
export const bypassLogin = (sourceId, userLogin, bypassToken, areaType, preOrderSessionId) => dispatch => {
    dispatch({
        type: CONNECT_USER_REQUEST,
    })

    // Build request body.
    const body = {
        identifiantSource: sourceId,
        identifiantCible: userLogin,
        jetonConnexion: bypassToken,
        typeEspace: areaType,
        idSessionPreCommande: preOrderSessionId ? preOrderSessionId : null,
    }

    return axios.post(AUTH_ENDPOINTS.bypassLogin, body).then(
        response => {
            dispatch({
                type: CONNECT_USER_SUCCESS,
                response: response.data,
            })
            dispatch({
                type: SET_BYPASSED_AUTHENTICATION,
            })
            addTokenToHeader(response.data.token)
            addTokenServiceAPIToHeader(response.data.token)

            return Promise.resolve()
        },
        error => {
            const message = getErrorMessage(error)
            dispatch({
                type: CONNECT_USER_FAILURE,
                message,
            })
            removeTokenFromHeader()
            removeTokenServiceAPIFromHeader()
        },
    )
}

export const recoverLoginBeneficiaryArea = (email, birthDate) => dispatch => {
    const body = {
        email,
        dateNaissance: birthDate,
    }

    const url = AUTH_ENDPOINTS.recoverLoginBeneficiaryArea

    return new Promise((resolve, reject) => {
        genericThunk(
            body,
            httpMethod.POST,
            dispatch,
            url,
            undefined,
            RequestInfo[RequestKeys.RECOVER_LOGIN_BENEFICIARY_AREA].name,
        )
            .then(response => resolve(response))
            .catch(error => reject(error))
    })
}
